import { Flex } from "antd";
import i18next from "i18next";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useAuthContext } from "../../../../contexts/AuthContext";
import query from "../../../../server/constants.api";
import logger from "../../../../server/server";
import useFetch from "../../../../server/useFetch";
import { renderSimpleObjects } from "../../../../utils/renders/render-helper";
import Circle from "../../Atomics/Circle";
import { NoData } from "../../Atomics/NoData";
import SimpleTooltip from "../../Atomics/SimpleTooltip";
import Spinner from "../../Atomics/Spinner";
import { ChartLegendComponent } from "../ChartLegendComponent/ChartLegendComponent";

const CircleWrapper = styled.div`
  position: absolute;
  z-index: ${(props) => props.zIndex};
  left: ${(props) => props.leftPercentage}%;
  top: ${(props) => props.topPercentage}%;
`;

const ChartLegend = styled.div`
  position: absolute;
  right: 3%;
  bottom: 3%;
`;

export const StackedFloorsComponent = ({
  building,
  metric,
  $height,
  setOrbitControls,
  onFloorClicked,
  ...props
}) => {
  useEffect(() => {
    refetch();
  }, [i18next.resolvedLanguage]);

  const mountRef = useRef();
  const simpleTooltipRef = useRef();
  const circleRefs = useRef([]);
  const [objectHovered, setObjectHovered] = useState();
  const [metricLegend, setMetricLegend] = useState();
  const { t } = useTranslation();
  const { user, tenant } = useAuthContext();

  const { data: buildingOverviewOBJ, refetch, isFetching } = useFetch(
    "/registry/3DPlant", "buildingDetailOBJ",
    {
      id: building?.id,
      type: "BUILDING",
    },
    building?.id ?? false, null, query.middlewareURL);

  useEffect(() => {
    if (!(buildingOverviewOBJ === undefined || buildingOverviewOBJ == null))
      if (Object.keys(buildingOverviewOBJ).length === 0) {
        logger.warn("User " + user?.Username + " Tenant: " + tenant + ", buildingOverviewOBJ is a empty object!");
      }
  }, [buildingOverviewOBJ]);

  const canUseWebGL = () =>
    typeof window !== "undefined" &&
    !!window.document.createElement("canvas").getContext("webgl");

  const onObjectHover = (object, top, left) => {
    simpleTooltipRef.current.style.left = `${top}px`;
    simpleTooltipRef.current.style.top = `${left}px`;
  };

  const loadBuildingFloors = (buildingOverviewOBJ) => {
    console.log(window.document.createElement("canvas"))
    if (canUseWebGL() && buildingOverviewOBJ) {
      let floors = buildingOverviewOBJ?.floors;
      try {
        if (floors) {
          const objectDiv = document.createElement("div");
          objectDiv.classList.add("object-div");
          objectDiv.style.setProperty("height", `inherit`);
          mountRef.current.appendChild(objectDiv);

          let camera = {
            fov: 10,
            near: 0.1,
            far: 1000,
            position: { x: 125, y: 70, z: 125 },
            lookAt: { x: 0, y: 0, z: 0 },
          };

          let ambLight = {
            color: "white",
            intensity: 0.2,
          };

          let lights = [
            { x: 15, y: 20, z: -26 },
            { x: 15, y: 36, z: 15 },
          ];
          let renderProps = {
            camera,
            ambLight,
            lights,
            handleResize: true,
            onHover: onObjectHover,
            setHovered: setObjectHovered,
            onClick: (clickedObject) => {
              onFloorClicked ? onFloorClicked(clickedObject?.floorId) : null;
            },
            onRightClick: (clickedObject) => { console.log("Right Clicked", clickedObject) },
            setOrbitControls: setOrbitControls ?? null,
          };

          setMetricLegend(
            buildingOverviewOBJ?.[metric]?.legend.map((item) => {
              return {
                circleColor: item?.color, // Using the color property for the circle
                label: t(item?.text), // Using the text property for the label
              };
            })
          );

          renderSimpleObjects(objectDiv, floors, metric, renderProps, t);
        }
      } catch (error) {
        logger.error("User " + user?.Username + " Tenant: " + tenant + ", " + `Error in WebGLRenderer ${error}`);
        throw new Error(`Error in WebGLRenderer ${error}`);
      }
    }
  };

  useEffect(() => {
    refetch();
  }, [metric]);

  useEffect(() => {
    if (!isFetching) {
      //cleanRenders(mountRef.current, "object-div");
      loadBuildingFloors(buildingOverviewOBJ);
    }
  }, [isFetching]);

  return (
    <>
      {!isFetching ? (
        <>
          {buildingOverviewOBJ?.floors ? (
            <>
              <div
                style={{ width: "100%", height: $height ? $height : "100%" }}
                ref={mountRef}
                className={props?.className}
              >
                {buildingOverviewOBJ?.floors.map((item, index, array) => {
                  const overlapPercentage =
                    ((array.length - index) / array.length) * 80;
                  return (
                    <CircleWrapper
                      leftPercentage={32}
                      topPercentage={overlapPercentage}
                      key={index}
                    >
                      <Circle
                        number={index + 1}
                        width={"30px"}
                        height={"30px"}
                        className={`circle-${index + 1}`}
                        reference={(el) => (circleRefs.current[index] = el)}
                      ></Circle>
                    </CircleWrapper>
                  );
                })}
                <SimpleTooltip
                  reference={simpleTooltipRef}
                  display={objectHovered?.value ? "block" : "none"}
                  text={t(objectHovered?.label)}
                  value={objectHovered?.value}
                />
              </div>
              {metricLegend ? (
                <ChartLegend className="chart-legend">
                  <ChartLegendComponent data={metricLegend} />
                </ChartLegend>
              ) : null}
            </>
          ) : (
            <NoData></NoData>
          )}
        </>
      ) : (
        <Flex justify="center" styles={{ height: "100%" }}>
          <Spinner stroke="green" />
        </Flex>
      )}
    </>
  );
};
